// File: frontend/src/pages/admin/SuperAdminHub.jsx

import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import DashboardLayout from '../../components/DashboardLayout';
import { useAuth } from '../../context/AuthContext';
import { useSchool } from '../../context/SchoolContext';
import api from '../../utils/axios';

// Embedded Section Components
import WhiteLabelBranding from '../branding/BrandingSettings';
import UserManagement from '../settings/UserManagement';

const SuperAdminHub = () => {
  const { user } = useAuth();
  const { school } = useSchool();
  const [searchParams, setSearchParams] = useSearchParams();

  // Active Tab derived from URL query string (?tab=overview)
  const tabFromUrl = searchParams.get('tab') || 'overview';
  const [activeTab, setActiveTab] = useState(tabFromUrl);

  // States: Overview KPIs
  const [stats, setStats] = useState({
    totalSchools: 0,
    activeLicenses: 0,
    expiringLicenses: 0,
    estimatedMRR: 0,
    systemHealth: { db: 'Online', storage: 'Active (LAN)', api: 'Healthy 200 OK' },
    loading: true
  });

  // States: Client Schools Tab
  const [schools, setSchools] = useState([]);
  const [schoolSearch, setSchoolSearch] = useState('');
  const [schoolsLoading, setSchoolsLoading] = useState(true);
  const [schoolFeedback, setSchoolFeedback] = useState({ type: '', message: '' });

  // Provisioning Modal & Credential Summary Modal
  const [isProvisionModalOpen, setIsProvisionModalOpen] = useState(false);
  const [provisionSubmitting, setProvisionSubmitting] = useState(false);
  const [provisionModalError, setProvisionModalError] = useState('');
  const [provisionFormData, setProvisionFormData] = useState({
    name: '',
    phone: '',
    address: '',
    max_students: 500,
    plan_tier: 'pro',
    duration_months: 12,
    feature_ai_test_gen: 1,
    feature_id_cards: 1,
    feature_timetable: 1,
    principal_name: '',
    principal_email: '',
    principal_password: ''
  });
  const [createdCredentials, setCreatedCredentials] = useState(null);
  const [copiedSuccess, setCopiedSuccess] = useState(false);

  // Robust Clipboard Copy Handler with Fallback for HTTP / Mobile / LAN IPs
  const handleCopyCredentials = () => {
    if (!createdCredentials) return;
    const text = `MARKAZSCHOOL PRO - OFFICIAL TENANT ONBOARDING CREDENTIALS\n` +
      `===================================================\n` +
      `School Name: ${createdCredentials.school_name}\n` +
      `Tenant ID: School #${createdCredentials.school_id}\n` +
      `License Key: ${createdCredentials.license_key}\n` +
      `Plan Tier: ${createdCredentials.plan_tier ? createdCredentials.plan_tier.toUpperCase() : 'STANDARD'}\n` +
      `---------------------------------------------------\n` +
      `PRIMARY PRINCIPAL LOGIN CREDENTIALS:\n` +
      `Email: ${createdCredentials.principal_email}\n` +
      `Temporary Password: ${createdCredentials.temporary_password}\n` +
      `===================================================\n` +
      `Generated Date: ${new Date().toLocaleDateString()}\n`;

    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard.writeText(text).then(() => {
        setCopiedSuccess(true);
        setTimeout(() => setCopiedSuccess(false), 3000);
      }).catch(() => fallbackCopyText(text));
    } else {
      fallbackCopyText(text);
    }
  };

  const fallbackCopyText = (text) => {
    try {
      const textArea = document.createElement('textarea');
      textArea.value = text;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      textArea.style.top = '-999999px';
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopiedSuccess(true);
      setTimeout(() => setCopiedSuccess(false), 3000);
    } catch (err) {
      console.error('Fallback copy failed', err);
      alert(text);
    }
  };

  // Renewal Modal State
  const [renewModalSchool, setRenewModalSchool] = useState(null);
  const [renewMonths, setRenewMonths] = useState(12);
  const [renewPlanTier, setRenewPlanTier] = useState('pro');
  const [renewCustomDate, setRenewCustomDate] = useState('');
  const [renewSubmitting, setRenewSubmitting] = useState(false);

  // System Integrations & Audit Logs Tab
  const [auditLogs, setAuditLogs] = useState([]);
  const [auditLoading, setAuditLoading] = useState(false);
  const [systemConfig, setSystemConfig] = useState({
    smsGatewayKey: 'TWILIO_SECRET_LAN_88921',
    paymentKey: 'STRIPE_LIVE_KEY_PK_99182',
    uploadStoragePath: 'c:\\Projects\\school-ms\\backend\\uploads\\',
    localIp: '192.168.1.100:5000'
  });
  const [configSavedMsg, setConfigSavedMsg] = useState('');

  // Sync tab with URL parameter
  useEffect(() => {
    setActiveTab(tabFromUrl);
  }, [tabFromUrl]);

  const handleTabChange = (newTab) => {
    setActiveTab(newTab);
    setSearchParams({ tab: newTab });
  };

  // Fetch all schools and calculate cross-tenant KPIs
  const fetchSchoolsData = async () => {
    setSchoolsLoading(true);
    try {
      const response = await api.get('/api/v1/settings/all-schools');
      const allSchools = response.data?.data || [];
      setSchools(allSchools);

      // Calculate cross-tenant metrics
      const now = new Date();
      const thirtyDaysMs = 30 * 24 * 60 * 60 * 1000;
      let activeCount = 0;
      let expiringCount = 0;
      let mrrSum = 0;

      allSchools.forEach((s) => {
        if (s.is_licensed !== 0) activeCount++;
        
        // Expiry check (<30 days)
        if (s.license_expiry) {
          const expiryDate = new Date(s.license_expiry);
          const diff = expiryDate.getTime() - now.getTime();
          if (diff > 0 && diff <= thirtyDaysMs) {
            expiringCount++;
          }
        }

        // Estimated MRR calculation
        if (s.plan_tier === 'basic') mrrSum += 49;
        else if (s.plan_tier === 'pro') mrrSum += 99;
        else if (s.plan_tier === 'enterprise') mrrSum += 199;
        else mrrSum += 49;
      });

      setStats({
        totalSchools: allSchools.length,
        activeLicenses: activeCount,
        expiringLicenses: expiringCount,
        estimatedMRR: mrrSum,
        systemHealth: { db: 'Online (MySQL Connected)', storage: 'Active (LAN Local Files)', api: 'Healthy 200 OK' },
        loading: false
      });
    } catch (err) {
      console.error('Error fetching schools:', err);
      setStats((prev) => ({ ...prev, loading: false }));
    } finally {
      setSchoolsLoading(false);
    }
  };

  // Fetch Audit Logs for Tab 5
  const fetchAuditLogsData = async () => {
    setAuditLoading(true);
    try {
      const res = await api.get('/api/v1/settings/audit-logs');
      setAuditLogs(res.data?.data || []);
    } catch (err) {
      console.error('Error fetching audit logs:', err);
    } finally {
      setAuditLoading(false);
    }
  };

  // Fetch System Integration Config for Tab 5
  const fetchSystemConfig = async () => {
    try {
      const res = await api.get('/api/v1/settings/system-config');
      if (res.data?.data) {
        setSystemConfig(res.data.data);
      }
    } catch (err) {
      console.error('Error fetching system config:', err);
    }
  };

  // Save Persistent System Config
  const handleSaveSystemConfig = async () => {
    try {
      const res = await api.put('/api/v1/settings/system-config', systemConfig);
      setConfigSavedMsg(res.data?.message || '✅ Integration settings saved!');
    } catch (err) {
      console.error('Error saving system config:', err);
    }
  };

  // Helper: Render Dynamic Expiry Warning Badges
  const renderExpiryStatusBadge = (expiryDateStr) => {
    if (!expiryDateStr) {
      return <span className="px-2 py-0.5 bg-purple-100 text-purple-800 text-[10px] font-bold rounded-full border border-purple-200">♾️ Lifetime / Unset</span>;
    }
    const expiryDate = new Date(expiryDateStr);
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const diffMs = expiryDate.getTime() - today.getTime();
    const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays < 0) {
      return (
        <div className="space-y-0.5">
          <div className="text-[11px] font-extrabold text-rose-700">Expires: {expiryDate.toLocaleDateString()}</div>
          <span className="inline-block px-2 py-0.5 bg-rose-100 text-rose-800 text-[10px] font-black rounded-full border border-rose-300">
            ⚠️ EXPIRED ({Math.abs(diffDays)}d ago)
          </span>
        </div>
      );
    } else if (diffDays <= 30) {
      return (
        <div className="space-y-0.5">
          <div className="text-[11px] font-extrabold text-amber-700">Expires: {expiryDate.toLocaleDateString()}</div>
          <span className="inline-block px-2 py-0.5 bg-amber-100 text-amber-900 text-[10px] font-black rounded-full border border-amber-300">
            ⏳ EXPIRING SOON ({diffDays}d left)
          </span>
        </div>
      );
    } else {
      return (
        <div className="space-y-0.5">
          <div className="text-[11px] font-semibold text-slate-600">Expires: {expiryDate.toLocaleDateString()}</div>
          <span className="inline-block px-2 py-0.5 bg-emerald-50 text-emerald-700 text-[10px] font-bold rounded-full border border-emerald-200">
            🟢 Active ({diffDays}d left)
          </span>
        </div>
      );
    }
  };

  useEffect(() => {
    fetchSchoolsData();
    if (activeTab === 'system') {
      fetchAuditLogsData();
      fetchSystemConfig();
    }
  }, [activeTab]);

  // Handler: Toggle School Active vs Suspended Access Status
  const handleToggleSchoolStatus = async (schoolId, currentLicensedStatus) => {
    const newStatus = currentLicensedStatus === 1 ? 0 : 1;
    try {
      await api.put(`/api/v1/settings/schools/${schoolId}/status`, { is_licensed: newStatus });
      setSchools((prev) =>
        prev.map((s) => (s.id === schoolId ? { ...s, is_licensed: newStatus } : s))
      );
      setSchoolFeedback({
        type: 'success',
        message: `School #${schoolId} access status updated to ${newStatus === 1 ? 'ACTIVE 🟢' : 'SUSPENDED 🔴'}`
      });
    } catch (err) {
      setSchoolFeedback({ type: 'error', message: 'Failed to update school access status.' });
    }
  };

  // Handler: Export JSON Tenant Backup
  const handleExportBackup = async (schoolId, schoolName) => {
    try {
      const res = await api.get(`/api/v1/settings/schools/${schoolId}/backup`);
      if (res.data?.success) {
        const jsonStr = JSON.stringify(res.data.data, null, 2);
        const blob = new Blob([jsonStr], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `backup-school-${schoolId}-${Date.now()}.json`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        setSchoolFeedback({ type: 'success', message: `Downloaded JSON backup snapshot for "${schoolName}"!` });
      }
    } catch (err) {
      console.error('Error exporting backup:', err);
      setSchoolFeedback({ type: 'error', message: 'Failed to export backup.' });
    }
  };

  // Handler: Import / Restore JSON Tenant Backup
  const handleImportBackup = (schoolId, schoolName) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.json';
    input.onchange = async (e) => {
      const file = e.target.files?.[0];
      if (!file) return;
      const reader = new FileReader();
      reader.onload = async (evt) => {
        try {
          const jsonContent = JSON.parse(evt.target.result);
          const res = await api.post(`/api/v1/settings/schools/${schoolId}/restore`, jsonContent);
          if (res.data?.success) {
            setSchoolFeedback({
              type: 'success',
              message: res.data.message || `Data snapshot for "${schoolName}" restored successfully!`
            });
            fetchSchoolsData();
          }
        } catch (err) {
          setSchoolFeedback({
            type: 'error',
            message: err.response?.data?.message || 'Failed to restore backup snapshot. Invalid JSON file format.'
          });
        }
      };
      reader.readAsText(file);
    };
    input.click();
  };

  // Handler: Submit License Renewal Modal
  const handleRenewLicenseSubmit = async (e) => {
    e.preventDefault();
    if (!renewModalSchool) return;
    setRenewSubmitting(true);
    try {
      const payload = {
        months: renewMonths,
        custom_expiry: renewCustomDate || null,
        plan_tier: renewPlanTier
      };
      const res = await api.put(`/api/v1/settings/schools/${renewModalSchool.id}/renew`, payload);
      if (res.data?.success) {
        setSchoolFeedback({ type: 'success', message: res.data.message });
        setRenewModalSchool(null);
        fetchSchoolsData();
      }
    } catch (err) {
      console.error('Error renewing license:', err);
      setSchoolFeedback({ type: 'error', message: 'Failed to renew school license.' });
    } finally {
      setRenewSubmitting(false);
    }
  };

  // Handler: Feature Toggle Update
  const handleFeatureToggle = async (schoolId, featureKey, currentValue) => {
    try {
      const updatedValue = currentValue === 1 ? 0 : 1;
      const targetSchool = schools.find((s) => s.id === schoolId);
      if (!targetSchool) return;

      const payload = {
        feature_ai_insights: featureKey === 'feature_ai_insights' ? updatedValue : targetSchool.feature_ai_insights,
        feature_ai_test_gen: featureKey === 'feature_ai_test_gen' ? updatedValue : targetSchool.feature_ai_test_gen,
        feature_id_cards: featureKey === 'feature_id_cards' ? updatedValue : targetSchool.feature_id_cards,
        feature_timetable: featureKey === 'feature_timetable' ? updatedValue : targetSchool.feature_timetable
      };

      await api.put(`/api/v1/settings/schools/${schoolId}/features`, payload);
      setSchools((prev) =>
        prev.map((s) => (s.id === schoolId ? { ...s, [featureKey]: updatedValue } : s))
      );
      setSchoolFeedback({ type: 'success', message: `Updated feature settings for School #${schoolId}!` });
    } catch (err) {
      setSchoolFeedback({ type: 'error', message: 'Failed to update feature settings.' });
    }
  };

  // Handler: Single-Step Provision Submit
  const handleProvisionSubmit = async (e) => {
    e.preventDefault();
    setProvisionSubmitting(true);
    setProvisionModalError('');
    setSchoolFeedback({ type: '', message: '' });

    try {
      const response = await api.post('/api/v1/settings/schools/provision', provisionFormData);
      if (response.data?.success) {
        setCreatedCredentials(response.data.data);
        setIsProvisionModalOpen(false);
        setProvisionModalError('');
        fetchSchoolsData();
        // Reset form
        setProvisionFormData({
          name: '',
          phone: '',
          address: '',
          max_students: 500,
          plan_tier: 'pro',
          duration_months: 12,
          feature_ai_test_gen: 1,
          feature_id_cards: 1,
          feature_timetable: 1,
          principal_name: '',
          principal_email: '',
          principal_password: ''
        });
      }
    } catch (err) {
      console.error('Error provisioning school:', err);
      const serverMsg = err.response?.data?.message || err.message || 'Failed to provision school & principal account.';
      setProvisionModalError(serverMsg);
      setSchoolFeedback({
        type: 'error',
        message: serverMsg
      });
    } finally {
      setProvisionSubmitting(false);
    }
  };

  // Handler: Delete School
  const handleDeleteSchool = async (schoolId, schoolName) => {
    if (!window.confirm(`⚠️ Are you sure you want to delete "${schoolName}" (#${schoolId})? This cannot be undone.`)) return;
    try {
      await api.delete(`/api/v1/settings/schools/${schoolId}`);
      setSchoolFeedback({ type: 'success', message: `School "${schoolName}" deleted successfully.` });
      fetchSchoolsData();
    } catch (err) {
      setSchoolFeedback({ type: 'error', message: 'Failed to delete tenant school.' });
    }
  };

  // Filtered Schools List
  const filteredSchools = schools.filter(
    (s) =>
      s.name?.toLowerCase().includes(schoolSearch.toLowerCase()) ||
      s.email?.toLowerCase().includes(schoolSearch.toLowerCase()) ||
      s.id?.toString().includes(schoolSearch)
  );

  return (
    <DashboardLayout>
      <div className="space-y-6">
        
        {/* Master Control Desk Header Banner */}
        <div 
          style={{
            background: `linear-gradient(135deg, ${school?.secondary_color || '#0f172a'} 0%, ${school?.primary_color || '#1e1b4b'} 100%)`
          }}
          className="text-white rounded-3xl p-6 sm:p-8 shadow-xl border border-purple-500/20 flex flex-col md:flex-row md:items-center justify-between gap-6"
        >
          <div>
            <div className="flex items-center space-x-2 mb-2">
              <span className="px-3 py-1 bg-amber-500/20 text-amber-400 text-[10px] font-black rounded-full border border-amber-500/30 uppercase tracking-widest">
                Master Super Admin HQ ⚡
              </span>
              <span className="text-purple-200 text-xs font-semibold">
                • Multi-Tenant Vendor Desk
              </span>
            </div>
            <h1 className="text-2xl sm:text-4xl font-black tracking-tight">
              Super Admin Control Center 🎛️
            </h1>
            <p className="text-purple-200 text-xs sm:text-sm mt-1.5 max-w-2xl leading-relaxed">
              Unified SaaS control center for provisioning tenant schools, managing plan licenses, default white-label branding, user administrative access, and system audit logs.
            </p>
          </div>

          <div className="flex items-center gap-3 shrink-0">
            <button
              onClick={() => setIsProvisionModalOpen(true)}
              className="px-5 py-3 bg-gradient-to-r from-amber-500 to-amber-600 hover:brightness-110 text-slate-950 font-black text-xs rounded-2xl shadow-xl transition flex items-center space-x-2"
            >
              <span className="text-base">➕</span>
              <span>Provision New School</span>
            </button>
          </div>
        </div>

        {/* 5 Horizontal Navigation Tabs */}
        <div className="bg-white rounded-2xl p-2 border border-slate-200 shadow-sm flex items-center space-x-1.5 overflow-x-auto">
          {[
            { id: 'overview', label: '📊 Executive Overview' },
            { id: 'schools', label: '🎛️ Client Schools & Licenses' },
            { id: 'users', label: '👥 User Access Control' },
            { id: 'branding', label: '🎨 Platform Branding' },
            { id: 'system', label: '⚙️ System & Audit Logs' }
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => handleTabChange(tab.id)}
              className={`px-5 py-3 rounded-xl text-xs font-extrabold transition whitespace-nowrap ${
                activeTab === tab.id
                  ? 'bg-slate-900 text-amber-400 shadow-md'
                  : 'text-slate-600 hover:bg-slate-100'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* ========================================================================= */}
        {/* TAB 1: EXECUTIVE OVERVIEW */}
        {/* ========================================================================= */}
        {activeTab === 'overview' && (
          <div className="space-y-6">
            {/* KPI Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="bg-white rounded-3xl p-6 border border-slate-200 shadow-sm space-y-1">
                <div className="flex items-center justify-between text-xs text-slate-500 font-bold">
                  <span>Registered Schools</span>
                  <span className="text-xl">🏫</span>
                </div>
                <div className="text-3xl font-black text-slate-900">
                  {stats.loading ? '...' : stats.totalSchools}
                </div>
                <p className="text-[11px] text-emerald-600 font-semibold">Active Tenant Entities</p>
              </div>

              <div className="bg-white rounded-3xl p-6 border border-slate-200 shadow-sm space-y-1">
                <div className="flex items-center justify-between text-xs text-slate-500 font-bold">
                  <span>Active Subscriptions</span>
                  <span className="text-xl">🔑</span>
                </div>
                <div className="text-3xl font-black text-emerald-600">
                  {stats.loading ? '...' : stats.activeLicenses}
                </div>
                <p className="text-[11px] text-slate-500 font-medium">Valid License Keys</p>
              </div>

              <div className="bg-white rounded-3xl p-6 border border-slate-200 shadow-sm space-y-1">
                <div className="flex items-center justify-between text-xs text-slate-500 font-bold">
                  <span>Expiring in 30 Days</span>
                  <span className="text-xl">⏳</span>
                </div>
                <div className="text-3xl font-black text-amber-600">
                  {stats.loading ? '...' : stats.expiringLicenses}
                </div>
                <p className="text-[11px] text-amber-600 font-semibold">Requires Renewal Action</p>
              </div>

              <div className="bg-white rounded-3xl p-6 border border-slate-200 shadow-sm space-y-1">
                <div className="flex items-center justify-between text-xs text-slate-500 font-bold">
                  <span>Estimated MRR Revenue</span>
                  <span className="text-xl">💰</span>
                </div>
                <div className="text-3xl font-black text-indigo-600">
                  Rs. {stats.loading ? '...' : stats.estimatedMRR.toLocaleString()}
                </div>
                <p className="text-[11px] text-indigo-600 font-semibold">Monthly Recurring Revenue (PKR)</p>
              </div>
            </div>

            {/* System Health Status Box */}
            <div className="bg-gradient-to-r from-slate-900 to-indigo-950 rounded-3xl p-6 text-white shadow-xl flex flex-col md:flex-row items-center justify-between gap-4 border border-indigo-500/20">
              <div className="space-y-1">
                <div className="flex items-center space-x-2">
                  <span className="w-2.5 h-2.5 rounded-full bg-emerald-400 animate-pulse"></span>
                  <span className="text-xs font-mono uppercase tracking-widest text-emerald-400 font-bold">System Health Monitor</span>
                </div>
                <h3 className="text-lg font-black text-white">Database & API Gateway Operational</h3>
                <p className="text-xs text-slate-300">Local LAN Server Hosting / Cloud Deployment Adaptability Active.</p>
              </div>
              <div className="flex flex-wrap items-center gap-3 text-xs font-mono">
                <div className="bg-slate-800/80 px-3 py-1.5 rounded-xl border border-slate-700">
                  Database: <strong className="text-emerald-400">{stats.systemHealth.db}</strong>
                </div>
                <div className="bg-slate-800/80 px-3 py-1.5 rounded-xl border border-slate-700">
                  Storage: <strong className="text-amber-400">{stats.systemHealth.storage}</strong>
                </div>
                <div className="bg-slate-800/80 px-3 py-1.5 rounded-xl border border-slate-700">
                  Gateway: <strong className="text-indigo-400">{stats.systemHealth.api}</strong>
                </div>
              </div>
            </div>

            {/* Recent Registered Schools Quick Table */}
            <div className="bg-white rounded-3xl p-6 border border-slate-200 shadow-sm space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-extrabold text-slate-900 text-base">Recent Registered Client Schools</h3>
                  <p className="text-xs text-slate-500">Latest tenant onboarding records in MarkazSchool Pro</p>
                </div>
                <button
                  onClick={() => handleTabChange('schools')}
                  className="text-xs font-extrabold text-indigo-600 hover:text-indigo-800 transition"
                >
                  View All Schools →
                </button>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full text-left text-xs">
                  <thead className="bg-slate-50 text-slate-500 font-bold uppercase tracking-wider">
                    <tr>
                      <th className="p-3">ID</th>
                      <th className="p-3">School Name</th>
                      <th className="p-3">Plan Tier</th>
                      <th className="p-3">License Key</th>
                      <th className="p-3">Expiration Date</th>
                      <th className="p-3">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {schools.slice(0, 5).map((s) => (
                      <tr key={s.id} className="hover:bg-slate-50 transition">
                        <td className="p-3 font-mono font-bold">#{s.id}</td>
                        <td className="p-3 font-bold text-slate-900">{s.name}</td>
                        <td className="p-3">
                          <span className={`px-2 py-0.5 rounded-full text-[10px] font-black uppercase ${
                            s.plan_tier === 'enterprise' ? 'bg-purple-100 text-purple-800' : s.plan_tier === 'pro' ? 'bg-blue-100 text-blue-800' : 'bg-slate-100 text-slate-700'
                          }`}>
                            {s.plan_tier || 'basic'}
                          </span>
                        </td>
                        <td className="p-3 font-mono text-[11px] text-slate-800 font-semibold">{s.license_key || 'MSPRO-FREE'}</td>
                        <td className="p-3 font-medium text-slate-600">{renderExpiryStatusBadge(s.license_expiry)}</td>
                        <td className="p-3">
                          <span className={`px-2 py-0.5 text-[10px] font-bold rounded-full ${
                            s.is_licensed !== 0 ? 'bg-emerald-100 text-emerald-800' : 'bg-rose-100 text-rose-800'
                          }`}>
                            {s.is_licensed !== 0 ? '🟢 Active' : '🔴 Suspended'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* ========================================================================= */}
        {/* TAB 2: CLIENT SCHOOLS & LICENSES */}
        {/* ========================================================================= */}
        {activeTab === 'schools' && (
          <div className="space-y-6">
            {schoolFeedback.message && (
              <div className={`p-4 rounded-2xl text-xs font-bold flex items-center justify-between ${
                schoolFeedback.type === 'success' ? 'bg-emerald-50 text-emerald-800 border border-emerald-200' : 'bg-rose-50 text-rose-800 border border-rose-200'
              }`}>
                <span>{schoolFeedback.message}</span>
                <button onClick={() => setSchoolFeedback({ type: '', message: '' })} className="font-bold text-base">&times;</button>
              </div>
            )}

            {/* Controls Bar */}
            <div className="bg-white rounded-3xl p-4 border border-slate-200 shadow-sm flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="relative w-full sm:w-80">
                <input
                  type="text"
                  placeholder="Search by school name, ID, or email..."
                  value={schoolSearch}
                  onChange={(e) => setSchoolSearch(e.target.value)}
                  className="w-full pl-9 pr-4 py-2.5 bg-slate-50 border border-slate-200 rounded-2xl text-xs font-semibold focus:outline-none focus:ring-2 focus:ring-purple-600"
                />
                <span className="absolute left-3 top-2.5 text-slate-400 text-sm">🔍</span>
              </div>

              <button
                onClick={() => setIsProvisionModalOpen(true)}
                className="w-full sm:w-auto px-5 py-2.5 bg-purple-700 hover:bg-purple-800 text-white font-extrabold text-xs rounded-2xl shadow-lg transition flex items-center justify-center space-x-2"
              >
                <span>➕ Provision New Client School</span>
              </button>
            </div>

            {/* Searchable Table */}
            <div className="bg-white rounded-3xl border border-slate-200 shadow-sm overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-left text-xs">
                  <thead className="bg-slate-900 text-amber-400 font-bold uppercase tracking-wider">
                    <tr>
                      <th className="p-4">School Details</th>
                      <th className="p-4">Plan & Access Status</th>
                      <th className="p-4">License Key & Expiration</th>
                      <th className="p-4">Module Toggles</th>
                      <th className="p-4">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {schoolsLoading ? (
                      <tr>
                        <td colSpan="5" className="p-8 text-center text-slate-500 font-bold">
                          Loading registered client schools...
                        </td>
                      </tr>
                    ) : filteredSchools.length === 0 ? (
                      <tr>
                        <td colSpan="5" className="p-8 text-center text-slate-500 font-bold">
                          No tenant schools match search.
                        </td>
                      </tr>
                    ) : (
                      filteredSchools.map((s) => (
                        <tr key={s.id} className="hover:bg-slate-50 transition">
                          <td className="p-4 space-y-1">
                            <div className="font-extrabold text-slate-900 text-sm">{s.name}</div>
                            <div className="text-[11px] text-slate-500 font-medium">ID #{s.id} • {s.phone || 'No phone'}</div>
                            <div className="text-[11px] text-slate-500">{s.email || 'No email registered'}</div>
                          </td>

                          <td className="p-4 space-y-2">
                            <div className="flex items-center space-x-2">
                              <span className={`px-2.5 py-1 rounded-xl text-[10px] font-black uppercase ${
                                s.plan_tier === 'enterprise' ? 'bg-purple-100 text-purple-800 border border-purple-200' : s.plan_tier === 'pro' ? 'bg-blue-100 text-blue-800 border border-blue-200' : 'bg-slate-100 text-slate-700 border border-slate-200'
                              }`}>
                                {s.plan_tier || 'basic'}
                              </span>
                            </div>
                            <button
                              onClick={() => handleToggleSchoolStatus(s.id, s.is_licensed)}
                              className={`px-2.5 py-1 rounded-xl text-[10px] font-extrabold flex items-center space-x-1 border transition ${
                                s.is_licensed !== 0 ? 'bg-emerald-50 text-emerald-800 border-emerald-300 hover:bg-emerald-100' : 'bg-rose-50 text-rose-800 border-rose-300 hover:bg-rose-100'
                              }`}
                              title="Click to Toggle Tenant Suspension"
                            >
                              <span>{s.is_licensed !== 0 ? '🟢 Active Access' : '🔴 Suspended'}</span>
                            </button>
                          </td>

                          <td className="p-4 space-y-1">
                            <div className="font-mono text-[11px] font-bold text-slate-800">{s.license_key || 'MSPRO-FREE'}</div>
                            {renderExpiryStatusBadge(s.license_expiry)}
                          </td>

                          <td className="p-4">
                            <div className="flex flex-wrap gap-1.5">
                              <button
                                onClick={() => handleFeatureToggle(s.id, 'feature_ai_test_gen', s.feature_ai_test_gen)}
                                className={`px-2 py-1 rounded-lg text-[10px] font-bold transition ${s.feature_ai_test_gen ? 'bg-purple-600 text-white' : 'bg-slate-200 text-slate-500'}`}
                              >
                                AI Tests: {s.feature_ai_test_gen ? 'ON' : 'OFF'}
                              </button>
                              <button
                                onClick={() => handleFeatureToggle(s.id, 'feature_id_cards', s.feature_id_cards)}
                                className={`px-2 py-1 rounded-lg text-[10px] font-bold transition ${s.feature_id_cards ? 'bg-blue-600 text-white' : 'bg-slate-200 text-slate-500'}`}
                              >
                                ID Cards: {s.feature_id_cards ? 'ON' : 'OFF'}
                              </button>
                              <button
                                onClick={() => handleFeatureToggle(s.id, 'feature_timetable', s.feature_timetable)}
                                className={`px-2 py-1 rounded-lg text-[10px] font-bold transition ${s.feature_timetable ? 'bg-emerald-600 text-white' : 'bg-slate-200 text-slate-500'}`}
                              >
                                Timetable: {s.feature_timetable ? 'ON' : 'OFF'}
                              </button>
                            </div>
                          </td>

                          <td className="p-4">
                            <div className="flex items-center space-x-1.5">
                              <button
                                onClick={() => {
                                  setRenewModalSchool(s);
                                  setRenewPlanTier(s.plan_tier || 'pro');
                                  setRenewMonths(12);
                                  setRenewCustomDate('');
                                }}
                                className="px-2.5 py-1 bg-amber-50 hover:bg-amber-100 text-amber-900 border border-amber-200 font-extrabold text-[10px] rounded-xl transition"
                                title="Renew / Extend License"
                              >
                                🔑 Renew
                              </button>
                              <button
                                onClick={() => handleExportBackup(s.id, s.name)}
                                className="px-2.5 py-1 bg-blue-50 hover:bg-blue-100 text-blue-900 border border-blue-200 font-extrabold text-[10px] rounded-xl transition"
                                title="Download Data Backup"
                              >
                                📥 Backup
                              </button>
                              <button
                                onClick={() => handleImportBackup(s.id, s.name)}
                                className="px-2.5 py-1 bg-purple-50 hover:bg-purple-100 text-purple-900 border border-purple-200 font-extrabold text-[10px] rounded-xl transition"
                                title="Restore / Import JSON Backup"
                              >
                                📤 Restore
                              </button>
                              <button
                                onClick={() => handleDeleteSchool(s.id, s.name)}
                                className="px-2.5 py-1 bg-rose-50 hover:bg-rose-100 text-rose-700 border border-rose-200 font-extrabold text-[10px] rounded-xl transition"
                                title="Delete Tenant School"
                              >
                                🗑️ Delete
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* ========================================================================= */}
        {/* TAB 3: USER ACCESS CONTROL */}
        {/* ========================================================================= */}
        {activeTab === 'users' && (
          <div className="bg-white rounded-3xl p-6 border border-slate-200 shadow-sm">
            <UserManagement embedded={true} />
          </div>
        )}

        {/* ========================================================================= */}
        {/* TAB 4: PLATFORM BRANDING */}
        {/* ========================================================================= */}
        {activeTab === 'branding' && (
          <div className="bg-white rounded-3xl p-6 border border-slate-200 shadow-sm">
            <WhiteLabelBranding embedded={true} />
          </div>
        )}

        {/* ========================================================================= */}
        {/* TAB 5: SYSTEM INTEGRATIONS & AUDIT LOGS */}
        {/* ========================================================================= */}
        {activeTab === 'system' && (
          <div className="space-y-6">
            {/* System Configuration Box */}
            <div className="bg-white rounded-3xl p-6 border border-slate-200 shadow-sm space-y-4">
              <div>
                <h3 className="font-extrabold text-slate-900 text-base">⚙️ Master System Integrations & Gateway Keys</h3>
                <p className="text-xs text-slate-500">Configure global SMS gateway, payment processors, and storage directories for LAN & cloud hosting.</p>
              </div>

              {configSavedMsg && (
                <div className="p-3 bg-emerald-50 text-emerald-800 text-xs font-bold rounded-xl border border-emerald-200">
                  {configSavedMsg}
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-slate-700 mb-1">SMS Gateway API Key / Secret</label>
                  <input
                    type="text"
                    value={systemConfig.smsGatewayKey}
                    onChange={(e) => setSystemConfig({ ...systemConfig, smsGatewayKey: e.target.value })}
                    className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3.5 py-2 text-xs font-mono"
                  />
                </div>

                <div>
                  <label className="block text-xs font-bold text-slate-700 mb-1">Payment Gateway Secret Key</label>
                  <input
                    type="text"
                    value={systemConfig.paymentKey}
                    onChange={(e) => setSystemConfig({ ...systemConfig, paymentKey: e.target.value })}
                    className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3.5 py-2 text-xs font-mono"
                  />
                </div>

                <div>
                  <label className="block text-xs font-bold text-slate-700 mb-1">Local / Cloud Media Upload Path</label>
                  <input
                    type="text"
                    value={systemConfig.uploadStoragePath}
                    onChange={(e) => setSystemConfig({ ...systemConfig, uploadStoragePath: e.target.value })}
                    className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3.5 py-2 text-xs font-mono"
                  />
                </div>

                <div>
                  <label className="block text-xs font-bold text-slate-700 mb-1">Local Network Host IP Binding</label>
                  <input
                    type="text"
                    value={systemConfig.localIp}
                    onChange={(e) => setSystemConfig({ ...systemConfig, localIp: e.target.value })}
                    className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3.5 py-2 text-xs font-mono"
                  />
                </div>
              </div>

              <div className="flex justify-end">
                <button
                  type="button"
                  onClick={handleSaveSystemConfig}
                  className="px-5 py-2.5 bg-slate-900 hover:bg-slate-800 text-amber-400 font-extrabold text-xs rounded-xl shadow-md transition"
                >
                  Save Integration Settings
                </button>
              </div>
            </div>

            {/* Audit Logs Table */}
            <div className="bg-white rounded-3xl p-6 border border-slate-200 shadow-sm space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-extrabold text-slate-900 text-base">📋 System Action Audit Logs</h3>
                  <p className="text-xs text-slate-500">Real-time log of administrative and state-changing actions executed across tenants.</p>
                </div>
                <button
                  onClick={fetchAuditLogsData}
                  className="px-3 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold text-xs rounded-xl transition"
                >
                  🔄 Refresh Logs
                </button>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full text-left text-xs">
                  <thead className="bg-slate-50 text-slate-500 font-bold uppercase tracking-wider">
                    <tr>
                      <th className="p-3">Timestamp</th>
                      <th className="p-3">User Email</th>
                      <th className="p-3">Tenant School</th>
                      <th className="p-3">Action Executed</th>
                      <th className="p-3">IP Address</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 font-mono text-[11px]">
                    {auditLoading ? (
                      <tr>
                        <td colSpan="5" className="p-6 text-center text-slate-500 font-sans font-bold">
                          Loading audit trail...
                        </td>
                      </tr>
                    ) : auditLogs.length === 0 ? (
                      <tr>
                        <td colSpan="5" className="p-6 text-center text-slate-500 font-sans font-bold">
                          No audit log records available yet.
                        </td>
                      </tr>
                    ) : (
                      auditLogs.map((log) => (
                        <tr key={log.id} className="hover:bg-slate-50 transition">
                          <td className="p-3 text-slate-500">{new Date(log.created_at).toLocaleString()}</td>
                          <td className="p-3 font-bold text-slate-900 font-sans">{log.user_email || 'System'}</td>
                          <td className="p-3 text-purple-700 font-sans font-semibold">{log.school_name || 'Global'}</td>
                          <td className="p-3 text-slate-800 font-bold">{log.action}</td>
                          <td className="p-3 text-slate-500">{log.ip || '127.0.0.1'}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

      </div>

      {/* ========================================================================= */}
      {/* SINGLE-STEP SCHOOL + PRINCIPAL PROVISIONING MODAL */}
      {/* ========================================================================= */}
      {isProvisionModalOpen && (
        <div className="fixed inset-0 z-50 bg-slate-950/70 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl p-6 sm:p-8 max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl border border-purple-100 space-y-6">
            
            <div className="flex items-center justify-between border-b border-purple-100 pb-4">
              <div>
                <span className="px-2.5 py-0.5 bg-amber-500/20 text-amber-800 text-[10px] font-black rounded-full uppercase tracking-widest border border-amber-500/30">
                  Single-Step Onboarding
                </span>
                <h3 className="text-xl font-black text-slate-900 mt-1">Register & Provision Tenant School 🏫</h3>
                <p className="text-xs text-slate-500">Provisions isolated database entry, assigns active license, and creates primary Principal account.</p>
              </div>
              <button
                onClick={() => setIsProvisionModalOpen(false)}
                className="w-8 h-8 rounded-full bg-slate-100 text-slate-500 hover:text-slate-900 flex items-center justify-center font-bold text-sm"
              >
                ✕
              </button>
            </div>

            {provisionModalError && (
              <div className="p-4 rounded-2xl bg-rose-50 border border-rose-200 text-rose-800 text-xs font-bold flex items-center justify-between shadow-xs">
                <div className="flex items-center space-x-2">
                  <span className="text-base">⚠️</span>
                  <span>{provisionModalError}</span>
                </div>
                <button type="button" onClick={() => setProvisionModalError('')} className="font-extrabold text-sm text-rose-500 hover:text-rose-900">&times;</button>
              </div>
            )}

            <form onSubmit={handleProvisionSubmit} className="space-y-6">
              
              {/* SECTION A: SCHOOL DETAILS */}
              <div className="space-y-3">
                <h4 className="text-xs font-black text-purple-950 uppercase tracking-wider flex items-center gap-1.5">
                  <span>🏫 1. School Information</span>
                </h4>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-bold text-slate-700 mb-1">
                      School Name <span className="text-rose-500">*</span>
                    </label>
                    <input
                      type="text"
                      required
                      placeholder="e.g. Greenwood International Academy"
                      value={provisionFormData.name}
                      onChange={(e) => setProvisionFormData({ ...provisionFormData, name: e.target.value })}
                      className="w-full bg-purple-50/40 border border-purple-200 rounded-xl px-3.5 py-2.5 text-xs font-semibold text-slate-900 focus:outline-none focus:ring-2 focus:ring-purple-600"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-slate-700 mb-1">Official Phone</label>
                    <input
                      type="text"
                      placeholder="e.g. +92 300 9876543"
                      value={provisionFormData.phone}
                      onChange={(e) => setProvisionFormData({ ...provisionFormData, phone: e.target.value })}
                      className="w-full bg-purple-50/40 border border-purple-200 rounded-xl px-3.5 py-2.5 text-xs font-semibold text-slate-900 focus:outline-none focus:ring-2 focus:ring-purple-600"
                    />
                  </div>

                  <div className="sm:col-span-2">
                    <label className="block text-xs font-bold text-slate-700 mb-1">Campus Address / City</label>
                    <input
                      type="text"
                      placeholder="e.g. Sector H-8/4, Islamabad"
                      value={provisionFormData.address}
                      onChange={(e) => setProvisionFormData({ ...provisionFormData, address: e.target.value })}
                      className="w-full bg-purple-50/40 border border-purple-200 rounded-xl px-3.5 py-2.5 text-xs font-semibold text-slate-900 focus:outline-none focus:ring-2 focus:ring-purple-600"
                    />
                  </div>
                </div>
              </div>

              {/* SECTION B: LICENSE & PLAN TIER */}
              <div className="space-y-3 pt-3 border-t border-purple-50">
                <h4 className="text-xs font-black text-purple-950 uppercase tracking-wider flex items-center gap-1.5">
                  <span>🔑 2. License & Tier Setup</span>
                </h4>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-bold text-slate-700 mb-1">Plan Tier</label>
                    <select
                      value={provisionFormData.plan_tier}
                      onChange={(e) => {
                        const selectedTier = e.target.value;
                        setProvisionFormData((prev) => ({
                          ...prev,
                          plan_tier: selectedTier,
                          feature_ai_insights: selectedTier === 'enterprise' ? 1 : 0,
                          feature_ai_test_gen: selectedTier === 'basic' ? 0 : 1,
                          feature_id_cards: selectedTier === 'basic' ? 0 : 1,
                          feature_timetable: selectedTier === 'basic' ? 0 : 1
                        }));
                      }}
                      className="w-full bg-purple-50/40 border border-purple-200 rounded-xl px-3.5 py-2.5 text-xs font-bold text-purple-900 focus:outline-none focus:ring-2 focus:ring-purple-600 cursor-pointer"
                    >
                      <option value="basic">Basic Plan (Core ERP)</option>
                      <option value="pro">Pro Plan (+ ID Cards & Timetable)</option>
                      <option value="enterprise">Enterprise AI Plan (+ Groq AI)</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-slate-700 mb-1">License Duration (Months)</label>
                    <input
                      type="number"
                      min="1"
                      max="60"
                      value={provisionFormData.duration_months}
                      onChange={(e) => setProvisionFormData({ ...provisionFormData, duration_months: e.target.value })}
                      className="w-full bg-purple-50/40 border border-purple-200 rounded-xl px-3.5 py-2.5 text-xs font-bold text-slate-900 focus:outline-none focus:ring-2 focus:ring-purple-600"
                    />
                  </div>
                </div>
              </div>

              {/* SECTION C: PRIMARY PRINCIPAL ACCOUNT */}
              <div className="space-y-3 pt-3 border-t border-purple-50">
                <h4 className="text-xs font-black text-purple-950 uppercase tracking-wider flex items-center gap-1.5">
                  <span>👤 3. Primary Principal Account</span>
                </h4>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-bold text-slate-700 mb-1">
                      Principal Full Name <span className="text-rose-500">*</span>
                    </label>
                    <input
                      type="text"
                      required
                      placeholder="e.g. Principal Greenwood"
                      value={provisionFormData.principal_name}
                      onChange={(e) => setProvisionFormData({ ...provisionFormData, principal_name: e.target.value })}
                      className="w-full bg-purple-50/40 border border-purple-200 rounded-xl px-3.5 py-2.5 text-xs font-semibold text-slate-900 focus:outline-none focus:ring-2 focus:ring-purple-600"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-slate-700 mb-1">
                      Principal Email / Username <span className="text-rose-500">*</span>
                    </label>
                    <input
                      type="email"
                      required
                      placeholder="e.g. principal@greenwood.edu"
                      value={provisionFormData.principal_email}
                      onChange={(e) => setProvisionFormData({ ...provisionFormData, principal_email: e.target.value })}
                      className="w-full bg-purple-50/40 border border-purple-200 rounded-xl px-3.5 py-2.5 text-xs font-semibold text-slate-900 focus:outline-none focus:ring-2 focus:ring-purple-600"
                    />
                  </div>

                  <div className="sm:col-span-2">
                    <label className="block text-xs font-bold text-slate-700 mb-1">
                      Initial Temporary Password <span className="text-rose-500">*</span>
                    </label>
                    <input
                      type="text"
                      required
                      placeholder="e.g. Password123!"
                      value={provisionFormData.principal_password}
                      onChange={(e) => setProvisionFormData({ ...provisionFormData, principal_password: e.target.value })}
                      className="w-full bg-purple-50/40 border border-purple-200 rounded-xl px-3.5 py-2.5 text-xs font-mono font-bold text-slate-900 focus:outline-none focus:ring-2 focus:ring-purple-600"
                    />
                  </div>
                </div>
              </div>

              {/* Submit Buttons */}
              <div className="pt-4 flex items-center justify-end space-x-3 border-t border-slate-100">
                <button
                  type="button"
                  onClick={() => setIsProvisionModalOpen(false)}
                  className="px-4 py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold text-xs rounded-xl transition"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={provisionSubmitting}
                  className="px-6 py-2.5 bg-purple-700 hover:bg-purple-800 text-white font-extrabold text-xs rounded-xl shadow-lg transition flex items-center space-x-2"
                >
                  <span>{provisionSubmitting ? 'Provisioning...' : '✨ Provision School & Account'}</span>
                </button>
              </div>

            </form>
          </div>
        </div>
      )}

      {/* ========================================================================= */}
      {/* PRINTABLE / COPYABLE CREDENTIAL SUMMARY MODAL */}
      {/* ========================================================================= */}
      {createdCredentials && (
        <div className="fixed inset-0 z-50 bg-slate-950/80 backdrop-blur-xs flex items-center justify-center p-4">
          {/* Print Style Injector to eliminate top space gap and hide background/buttons */}
          <style>{`
            @media print {
              body * {
                visibility: hidden !important;
              }
              .print-credential-area, .print-credential-area * {
                visibility: visible !important;
              }
              .print-credential-area {
                position: absolute !important;
                left: 0 !important;
                top: 0 !important;
                width: 100% !important;
                margin: 0 !important;
                padding: 24px !important;
                background: #ffffff !important;
                color: #0f172a !important;
                box-shadow: none !important;
                border: 2px solid #0f172a !important;
                border-radius: 16px !important;
              }
              .print-credential-area .bg-slate-900 {
                background: #f8fafc !important;
                color: #0f172a !important;
                border: 1px solid #cbd5e1 !important;
              }
              .print-credential-area .text-white {
                color: #0f172a !important;
              }
              .print-credential-area .text-amber-400,
              .print-credential-area .text-amber-300 {
                color: #b45309 !important;
              }
              .print-credential-area .text-emerald-400 {
                color: #047857 !important;
              }
              .print-credential-area .text-purple-300 {
                color: #6b21a8 !important;
              }
              .print-credential-area .text-blue-300 {
                color: #1d4ed8 !important;
              }
              .no-print {
                display: none !important;
              }
            }
          `}</style>

          <div className="print-credential-area bg-white rounded-3xl p-6 sm:p-8 max-w-lg w-full shadow-2xl border border-emerald-200 space-y-6 animate-fadeIn">
            
            <div className="text-center space-y-2">
              <div className="w-12 h-12 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center text-2xl mx-auto font-black shadow-inner">
                ✓
              </div>
              <h3 className="text-xl font-black text-slate-900">Provisioning Successful! 🎉</h3>
              <p className="text-xs text-slate-500">Tenant school and primary Principal credentials generated below for manual off-system handoff.</p>
            </div>

            {/* Credential Card Output */}
            <div className="bg-slate-900 text-white p-5 rounded-2xl space-y-3 font-mono text-xs border border-slate-800 shadow-xl">
              <div className="flex justify-between border-b border-slate-800 pb-2">
                <span className="text-slate-400">School Name:</span>
                <span className="font-bold text-amber-400 font-sans">{createdCredentials.school_name}</span>
              </div>
              <div className="flex justify-between border-b border-slate-800 pb-2">
                <span className="text-slate-400">Tenant ID:</span>
                <span className="font-bold text-purple-300">School #{createdCredentials.school_id}</span>
              </div>
              <div className="flex justify-between border-b border-slate-800 pb-2">
                <span className="text-slate-400">License Key:</span>
                <span className="font-bold text-emerald-400">{createdCredentials.license_key}</span>
              </div>
              <div className="flex justify-between border-b border-slate-800 pb-2">
                <span className="text-slate-400">Plan Tier:</span>
                <span className="font-bold uppercase text-blue-300">{createdCredentials.plan_tier}</span>
              </div>
              <div className="flex justify-between border-b border-slate-800 pb-2">
                <span className="text-slate-400">Principal Email:</span>
                <span className="font-bold text-white font-sans">{createdCredentials.principal_email}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-400">Temp Password:</span>
                <span className="font-bold text-amber-300">{createdCredentials.temporary_password}</span>
              </div>
            </div>

            {/* Action Buttons (Hidden on Print) */}
            <div className="flex flex-col sm:flex-row items-center gap-3 no-print">
              <button
                type="button"
                onClick={handleCopyCredentials}
                className={`w-full sm:flex-1 py-3 text-xs font-extrabold rounded-xl shadow-md transition text-center flex items-center justify-center gap-1.5 ${
                  copiedSuccess
                    ? 'bg-emerald-600 text-white'
                    : 'bg-amber-500 hover:bg-amber-600 text-slate-950'
                }`}
              >
                {copiedSuccess ? (
                  <>
                    <span>✓</span>
                    <span>Credentials Copied!</span>
                  </>
                ) : (
                  <>
                    <span>📋</span>
                    <span>Copy All Credentials</span>
                  </>
                )}
              </button>

              <button
                type="button"
                onClick={() => window.print()}
                className="w-full sm:flex-1 py-3 bg-slate-100 hover:bg-slate-200 text-slate-800 font-extrabold text-xs rounded-xl transition text-center flex items-center justify-center gap-1.5"
              >
                <span>🖨️</span>
                <span>Print Summary</span>
              </button>
            </div>

            <button
              type="button"
              onClick={() => {
                setCreatedCredentials(null);
                setCopiedSuccess(false);
              }}
              className="w-full py-2 text-xs font-bold text-slate-400 hover:text-slate-600 transition no-print"
            >
              Done & Close
            </button>

          </div>
        </div>
      )}

      {/* ========================================================================= */}
      {/* 1-CLICK LICENSE RENEWAL MODAL */}
      {/* ========================================================================= */}
      {renewModalSchool && (
        <div className="fixed inset-0 z-50 bg-slate-950/70 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl p-6 sm:p-8 max-w-md w-full shadow-2xl border border-amber-200 space-y-6">
            
            <div className="flex items-center justify-between border-b border-amber-100 pb-4">
              <div>
                <span className="px-2.5 py-0.5 bg-amber-100 text-amber-900 text-[10px] font-black rounded-full uppercase tracking-widest border border-amber-300">
                  License Extension
                </span>
                <h3 className="text-lg font-black text-slate-900 mt-1">Renew License 🔑</h3>
                <p className="text-xs text-slate-500">{renewModalSchool.name} (ID #{renewModalSchool.id})</p>
              </div>
              <button
                onClick={() => setRenewModalSchool(null)}
                className="w-8 h-8 rounded-full bg-slate-100 text-slate-500 hover:text-slate-900 flex items-center justify-center font-bold text-sm"
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleRenewLicenseSubmit} className="space-y-4">
              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">Plan Tier</label>
                <select
                  value={renewPlanTier}
                  onChange={(e) => setRenewPlanTier(e.target.value)}
                  className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3.5 py-2 text-xs font-semibold text-slate-900 focus:outline-none focus:ring-2 focus:ring-amber-500"
                >
                  <option value="basic">Basic Plan</option>
                  <option value="pro">Pro Plan</option>
                  <option value="enterprise">Enterprise AI Plan</option>
                </select>
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">Quick Extension Duration</label>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => { setRenewMonths(12); setRenewCustomDate(''); }}
                    className={`py-2 px-3 rounded-xl text-xs font-extrabold border transition ${renewMonths === 12 && !renewCustomDate ? 'bg-amber-500 text-white border-amber-600' : 'bg-slate-50 text-slate-700 border-slate-200'}`}
                  >
                    +12 Months (1 Year)
                  </button>
                  <button
                    type="button"
                    onClick={() => { setRenewMonths(6); setRenewCustomDate(''); }}
                    className={`py-2 px-3 rounded-xl text-xs font-extrabold border transition ${renewMonths === 6 && !renewCustomDate ? 'bg-amber-500 text-white border-amber-600' : 'bg-slate-50 text-slate-700 border-slate-200'}`}
                  >
                    +6 Months
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">Or Set Exact Expiration Date</label>
                <input
                  type="date"
                  value={renewCustomDate}
                  onChange={(e) => setRenewCustomDate(e.target.value)}
                  className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3.5 py-2 text-xs font-semibold text-slate-900 focus:outline-none focus:ring-2 focus:ring-amber-500"
                />
              </div>

              <div className="flex items-center space-x-3 pt-2">
                <button
                  type="button"
                  onClick={() => setRenewModalSchool(null)}
                  className="flex-1 py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold text-xs rounded-xl transition"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={renewSubmitting}
                  className="flex-1 py-2.5 bg-amber-500 hover:bg-amber-600 text-slate-950 font-extrabold text-xs rounded-xl shadow-md transition"
                >
                  {renewSubmitting ? 'Extending...' : 'Confirm Renewal ✨'}
                </button>
              </div>
            </form>

          </div>
        </div>
      )}

    </DashboardLayout>
  );
};

export default SuperAdminHub;
